/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  getResyBlog,
  getResyBlogLocation,
  getResyBlogVenue,
} from '../api';

const getResyBlogDefaultThunk = createAsyncThunk(
  'blog/getResyBlogDefaultStatus',
  getResyBlog
);

const getResyBlogLocationThunk = createAsyncThunk(
  'blog/getResyBlogLocationStatus',
  ({ code, lang = 'en' }) => getResyBlogLocation(code, lang)
);

const getResyBlogVenueThunk = createAsyncThunk(
  'blog/getResyBlogVenueStatus',
  (venueId) => getResyBlogVenue(venueId)
);

const ALL = 'all';
const isReady = (slice, key) => !slice[key].isLoading && slice[key].isInited;
const isItemLoading = (slice, key) => slice[key] && slice[key].isLoading;

const processPost = (post) => ({
  image: post.image && post.image.sizes ? post.image.sizes['resy-md'].url : ' ',
  tag: {
    name: post.kicker_title || ' ',
    url: post.kicker_url,
  },
  title: post.title_short || ' ',
  subtitle: post.short_description || ' ',
  url: post.url,
});

const blogSlice = createSlice({
  name: 'blog',
  initialState: {
    blogPosts: [{ tag: {} }, { tag: {} }, { tag: {} }, { tag: {} }, { tag: {} }],
    blogNavLinks: {},
    blogContent: [],
    all: {
      isLoading: false,
      isInited: false,
    },
  },
  reducers: {
    updateLocationBlog: (state, action) => {
      const code = action.payload;
      const key = code || ALL;
      state.blogPosts = state[key].blogPosts;
      state.blogNavLinks = state[key].blogNavLinks;
    },
    updateVenueBlog: (state, action) => {
      const venueId = action.payload;
      state.blogContent = state[venueId].blogContent;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getResyBlogDefaultThunk.pending, (state) => {
      state.all.isLoading = true;
    });
    builder.addCase(getResyBlogDefaultThunk.fulfilled, (state, action) => {
      const { data } = action.payload;
      const posts = data['resy:related'].slice(0, 5);
      const blogLinks = {
        links: data['resy:menu'].localscene,
        willAppendCity: !!data['resy:info'].code,
      };
      state.all.blogPosts = posts.map(processPost);
      state.all.blogNavLinks = blogLinks;
      state.all.isLoading = false;
      state.all.isInited = true;
    });
    builder.addCase(getResyBlogDefaultThunk.rejected, (state, action) => {
      state.all.isLoading = false;
      state.all.isInited = true;
      state.all.error = action.payload;
    });

    builder.addCase(getResyBlogLocationThunk.pending, (state, action) => {
      const locationCode = action.meta.arg?.code;
      /* istanbul ignore else */
      if (!state[locationCode]) {
        state[locationCode] = {};
      }
      state[locationCode].isLoading = true;
      state[locationCode].isInited = false;
    });
    builder.addCase(getResyBlogLocationThunk.fulfilled, (state, action) => {
      const locationCode = action.meta.arg?.code;
      const response = action.payload.data;
      const data = response.length > 0 ? response[0] : response;
      if (data['resy:related']) {
        const posts = data['resy:related'].filter((post) => !post.app_only).slice(0, 5);
        const blogLinks = {
          cityName: data.title.rendered,
          slug: data.slug,
          links: data['resy:menu'].localscene,
          willAppendCity: !!data['resy:info'].code,
        };
        state[locationCode].blogPosts = posts.map(processPost);
        state[locationCode].blogNavLinks = blogLinks;
      } else {
        state[locationCode].blogPosts = state.all.blogPosts;
        state[locationCode].blogNavLinks = state.all.blogNavLinks;
      }
      state[locationCode].isLoading = false;
      state[locationCode].isInited = true;
    });
    builder.addCase(getResyBlogLocationThunk.rejected, (state, action) => {
      const locationCode = action.meta.arg?.code;
      state[locationCode].error = action.payload;
      // Use fallback data
      state[locationCode].blogPosts = state.all.blogPosts;
      state[locationCode].blogNavLinks = state.all.blogNavLinks;
      state[locationCode].isLoading = false;
      state[locationCode].isInited = true;
    });

    builder.addCase(getResyBlogVenueThunk.pending, (state, action) => {
      const id = action.meta?.arg;
      /* istanbul ignore else */
      if (!state[id]) {
        state[id] = {};
      }
      state[id].isLoading = true;
      state[id].isInited = false;
    });
    builder.addCase(getResyBlogVenueThunk.fulfilled, (state, action) => {
      const id = action.meta?.arg;
      const response = action.payload.data;
      const blogContent = response.length > 0 && response[0]['resy:related'] ? response[0]['resy:related'] : [];
      state[id].isLoading = false;
      state[id].isInited = true;
      state[id].blogContent = blogContent;
    });
    builder.addCase(getResyBlogVenueThunk.rejected, (state, action) => {
      const id = action.meta?.arg;
      state[id].isLoading = false;
      state[id].isInited = true;
      state[id].error = action.payload;
    });
  },
});

const { updateLocationBlog, updateVenueBlog } = blogSlice.actions;
const updateLocationBlogThunk = (code, lang = 'en') => (dispatch, getState) => {
  const { blog: slice } = getState();
  const key = code || ALL;
  if (slice[key] && isReady(slice, key)) {
    return dispatch(updateLocationBlog(key));
  }
  if (isReady(slice, ALL) && !isItemLoading(slice, code)) {
    return dispatch(getResyBlogLocationThunk({ code, lang }))
      .then(() => dispatch(updateLocationBlog(code)));
  }
  if (!(isItemLoading(slice, ALL) || isItemLoading(slice, code))) {
    return dispatch(getResyBlogDefaultThunk())
      .then(() => dispatch(getResyBlogLocationThunk({ code, lang })))
      .then(() => dispatch(updateLocationBlog(code)));
  }
  return Promise.resolve({});
};

const updateVenueBlogThunk = (venueId, isProd) => (dispatch, getState) => {
  const { blog: slice } = getState();
  const FALLBACK_VENUE_ID = '984';
  const id = isProd ? venueId : FALLBACK_VENUE_ID;
  if (slice[id] && isReady(slice, id)) {
    return dispatch(updateVenueBlog(id));
  }
  if (!slice[id]) {
    return dispatch(getResyBlogVenueThunk(id))
      .then(() => dispatch(updateVenueBlog(id)));
  }
  return Promise.resolve([]);
};

export {
  getResyBlogDefaultThunk,
  getResyBlogLocationThunk,
  getResyBlogVenueThunk,
  updateLocationBlogThunk,
  updateLocationBlog,
  updateVenueBlogThunk,
  updateVenueBlog,
};
export default blogSlice.reducer;
